import React, { useState, useEffect, useRef } from "react";
import AdminTopBar from "./AdminTopBar";
import { useNavigate } from "react-router-dom";
import { collection, getDocs, doc, query, where, updateDoc, deleteDoc } from "firebase/firestore"; // Updated imports
import { db, auth } from "../../Services/firebase-config.js"; // Added auth
import {
  DocumentTextIcon,
  CheckCircleIcon,
  DotsVerticalIcon,
} from "@heroicons/react/outline";

const AdminDashboard = () => {
  const [users, setUsers] = useState([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null); // To store the user whose VCs are being viewed
  const [selectedValueChains, setSelectedValueChains] = useState([]);
  const [isLoadingValueChains, setIsLoadingValueChains] = useState(false);
  const [deletedKibalos, setDeletedKibalos] = useState([]); // State for deleted items
  const [error, setError] = useState(null);

  const [openUserDropdown, setOpenUserDropdown] = useState(null); // UID of user with open dropdown
  const userActionDropdownRef = useRef(null);

  const navigate = useNavigate();

  // Fetch all users and all deleted kibalos
  useEffect(() => {
    const fetchUsersData = async () => {
      setIsLoadingUsers(true);
      setDeletedKibalos([]);
      setError(null);
      try {
        // This assumes 'users' is a top-level collection where each document ID is a user's UID
        // and each user document might contain profile information.
        // In a real-world scenario, admin might need special permissions or a dedicated 'user_profiles' collection.
        const usersCollectionRef = collection(db, "users");
        const usersSnapshot = await getDocs(usersCollectionRef);
        
        let allDeletedKibalos = [];
        const usersList = await Promise.all(usersSnapshot.docs.map(async (userDoc) => {
          const userData = userDoc.data();
          const kibalosRef = collection(db, "users", userDoc.id, "kibalos");
          
          // Get active kibalos for count
          const activeKibalosQuery = query(kibalosRef, where("isDeleted", "!=", true));
          const activeKibalosSnapshot = await getDocs(activeKibalosQuery);

          // Get deleted kibalos for the trash view
          const deletedKibalosQuery = query(kibalosRef, where("isDeleted", "==", true));
          const deletedKibalosSnapshot = await getDocs(deletedKibalosQuery);
          deletedKibalosSnapshot.forEach(kibaloDoc => {
            allDeletedKibalos.push({
              ...kibaloDoc.data(),
              id: kibaloDoc.id,
              userId: userDoc.id, // Keep track of which user it belongs to
            });
          });

          return {
            uid: userDoc.id,
            email: userData.email || "N/A", // Assuming email is stored directly
            displayName: userData.displayName || "N/A", // Assuming displayName is stored
            valueChainCount: activeKibalosSnapshot.size,
          };
        }));
        
        setUsers(usersList);
        setDeletedKibalos(allDeletedKibalos);
      } catch (err) {
        console.error("Error fetching users:", err);
        setError("Failed to fetch users. Please try again.");
      }
      setIsLoadingUsers(false);
    };

    fetchUsersData();
  }, []);

  // Function to close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userActionDropdownRef.current && !userActionDropdownRef.current.contains(event.target)) {
        setOpenUserDropdown(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openUserDropdown]);

  const toggleUserDropdown = (uid) => {
    setOpenUserDropdown(openUserDropdown === uid ? null : uid);
  };

  const handleViewValueChains = async (user) => {
    setSelectedUser(user);
    setIsLoadingValueChains(true);
    setSelectedValueChains([]);
    setOpenUserDropdown(null); // Close dropdown
    try {
      const vcCollectionRef = collection(db, "users", user.uid, "valueChains");
      const vcSnapshot = await getDocs(vcCollectionRef);
      const vcs = vcSnapshot.docs.map(vcDoc => ({
        id: vcDoc.id,
        name: vcDoc.data().name || "Unnamed Value Chain",
        // Add other VC summary data if needed
      }));
      setSelectedValueChains(vcs);
    } catch (error) {
      console.error("Error fetching value chains for user:", user.uid, error);
      setError(`Failed to fetch value chains for ${user.email || user.uid}.`);
    }
    setIsLoadingValueChains(false);
  };

  const handleViewVCAnalytics = (userId, vcId, vcName) => {
    // Navigate to a page similar to DashboardContent, but for a specific user's VC
    // This route and component would need to be set up to accept userId and vcId
    navigate(`/admin/value-chain-analytics`, { state: { userId, valueChainId: vcId, valueChainName: vcName } });
    console.log(`Navigate to analytics for User: ${userId}, VC: ${vcId} (${vcName})`);
  };

  const handleEditVCComplex = (userId, vcId, vcName) => {
     // Navigate to a page similar to ValueChainEditorComplex
    navigate(`/admin/edit-value-chain-complex`, { state: { userId, valueChainId: vcId, valueChainName: vcName } });
    console.log(`Navigate to complex editor for User: ${userId}, VC: ${vcId} (${vcName})`);
  }

  const handleRestoreKibalo = async (kibalo) => {
    if (!window.confirm(`Are you sure you want to restore "${kibalo.name}"?`)) return;
    const docRef = doc(db, "users", kibalo.userId, "kibalos", kibalo.id);
    try {
      await updateDoc(docRef, { isDeleted: false, deletedAt: null });
      setDeletedKibalos(prevDeleted => prevDeleted.filter(k => k.id !== kibalo.id));
      alert("Enterprise restored successfully!");
    } catch (error) {
      console.error("Error restoring enterprise:", error);
      setError("Failed to restore enterprise.");
    }
  };

  const handlePermanentDeleteKibalo = async (kibalo) => {
    if (!window.confirm(`Are you sure you want to permanently delete "${kibalo.name}"? This action cannot be undone.`)) return;
    const docRef = doc(db, "users", kibalo.userId, "kibalos", kibalo.id);
    try {
      await deleteDoc(docRef);
      setDeletedKibalos(deletedKibalos.filter(k => k.id !== kibalo.id));
      alert("Enterprise permanently deleted!");
    } catch (error) {
      console.error("Error permanently deleting enterprise:", error);
      setError("Failed to permanently delete enterprise.");
    }
  };

  const totalValueChains = users.reduce((acc, user) => acc + user.valueChainCount, 0);

  if (isLoadingUsers) {
    return (
      <>
        <AdminTopBar title="Admin Dashboard" />
        <div className="container mx-auto p-6 text-center">Loading users...</div>
      </>
    );
  }

  return (
    <>
      <AdminTopBar title="Admin Dashboard" />

      <div className="container mx-auto p-6 space-y-6">
        {error && <div className="bg-red-100 text-red-700 p-4 rounded mb-4">{error}</div>}
        
        {/* Summary Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-blue-100 text-blue-800 p-6  flex items-center justify-between">
            <div>
              <h3 className="text-2xl font-bold">Total Users</h3>
              <p className="text-lg mt-2">{users.length}</p>
            </div>
            <DocumentTextIcon className="h-12 w-12 text-blue-600" />
          </div>

          <div className="bg-green-100 text-green-800 p-6  flex items-center justify-between">
            <div>
              <h3 className="text-2xl font-bold">Total Value Chains</h3>
              <p className="text-lg mt-2">{totalValueChains}</p>
            </div>
            <CheckCircleIcon className="h-12 w-12 text-green-600" />
          </div>
        </div>

        {/* Users Table */}
        <div className="bg-white p-6 ">
          <h2 className="text-xl font-bold mb-4">User Management</h2>
          <table className="min-w-full bg-white border border-gray-300">
            <thead>
              <tr>
                <th className="py-2 px-4 border-b text-left">User ID</th>
                <th className="py-2 px-4 border-b text-left">Email</th>
                <th className="py-2 px-4 border-b text-left">Display Name</th>
                <th className="py-2 px-4 border-b text-left">Value Chains</th>
                <th className="py-2 px-4 border-b text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.uid}>
                  <td className="py-2 px-4 border-b truncate max-w-xs">{user.uid}</td>
                  <td className="py-2 px-4 border-b">{user.email}</td>
                  <td className="py-2 px-4 border-b">{user.displayName}</td>
                  <td className="py-2 px-4 border-b">
                    {user.valueChainCount}
                  </td>
                  <td className="py-2 px-4 border-b">
                    <div className="relative inline-block text-left">
                      <DotsVerticalIcon
                        className="w-5 h-5 cursor-pointer"
                        onClick={() => toggleUserDropdown(user.uid)}
                      />
                      {openUserDropdown === user.uid && (
                        <div ref={userActionDropdownRef} className="absolute right-0 mt-2 w-48 bg-white shadow-lg border border-gray-300 z-10 rounded-md">
                          <button
                            className="block px-4 py-2 text-gray-700 hover:bg-gray-100 w-full text-left"
                            onClick={() => handleViewValueChains(user)}
                          >
                            View Value Chains
                          </button>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Selected User's Value Chains */}
        {selectedUser && (
          <div className="bg-white p-6 mt-6 ">
            <h2 className="text-xl font-bold mb-4">
              Value Chains for {selectedUser.email || selectedUser.uid}
            </h2>
            {isLoadingValueChains ? (
              <p>Loading value chains...</p>
            ) : selectedValueChains.length > 0 ? (
              <table className="min-w-full bg-white border border-gray-300">
                <thead>
                  <tr>
                    <th className="py-2 px-4 border-b text-left">VC ID</th>
                    <th className="py-2 px-4 border-b text-left">Name</th>
                    <th className="py-2 px-4 border-b text-left">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedValueChains.map((vc) => (
                    <tr key={vc.id}>
                      <td className="py-2 px-4 border-b truncate max-w-xs">{vc.id}</td>
                      <td className="py-2 px-4 border-b">{vc.name}</td>
                      <td className="py-2 px-4 border-b">
                        <button
                          onClick={() => handleViewVCAnalytics(selectedUser.uid, vc.id, vc.name)}
                          className="text-sm text-blue-600 hover:underline mr-2"
                        >
                          Analytics
                        </button>
                        <button
                          onClick={() => handleEditVCComplex(selectedUser.uid, vc.id, vc.name)}
                          className="text-sm text-green-600 hover:underline"
                        >
                          Edit Complex
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p>No value chains found for this user.</p>
            )}
          </div>
        )}

        {/* Deleted Enterprises Section */}
        <div className="bg-white p-6 mt-6 ">
          <h2 className="text-xl font-bold mb-4">Deleted Enterprises (Trash)</h2>
          {deletedKibalos.length > 0 ? (
            <table className="min-w-full bg-white border border-gray-300">
              <thead>
                <tr>
                  <th className="py-2 px-4 border-b text-left">Enterprise Name</th>
                  <th className="py-2 px-4 border-b text-left">Owner Email</th>
                  <th className="py-2 px-4 border-b text-left">Date Deleted</th>
                  <th className="py-2 px-4 border-b text-left">Actions</th>
                </tr>
              </thead>
              <tbody>
                {deletedKibalos.map((kibalo) => (
                  <tr key={kibalo.id}>
                    <td className="py-2 px-4 border-b">{kibalo.name}</td>
                    <td className="py-2 px-4 border-b">{users.find(u => u.uid === kibalo.userId)?.email || 'N/A'}</td>
                    <td className="py-2 px-4 border-b">{kibalo.deletedAt ? new Date(kibalo.deletedAt).toLocaleDateString() : 'N/A'}</td>
                    <td className="py-2 px-4 border-b">
                      <button
                        onClick={() => handleRestoreKibalo(kibalo)}
                        className="text-sm text-green-600 hover:underline mr-4"
                      >
                        Restore
                      </button>
                      <button
                        onClick={() => handlePermanentDeleteKibalo(kibalo)}
                        className="text-sm text-red-600 hover:underline"
                      >
                        Delete Permanently
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (<p>No deleted enterprises.</p>)}
        </div>
      </div>
    </>
  );
};

export default AdminDashboard;
